import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import DescriptionStepContext, { DescriptionStepFormData, DescriptionStepProvider } from './description_step/description_step_context'
import { LocationStepContext, LocationStepFormData, LocationStepProvider } from './location_step/location_step_context'
import AvailabilityStepContext, { AvailabilityStepFormData, AvailabilityStepProvider } from './avaliability_step/availability_step_context'
import PaymentStepContext, { PaymentStepProvider } from './payment_step/payment_step_context'
import PhotoStepContext, { PhotoStepFormData, PhotoStepProvider } from './photo_step/photo_step_context'
import useCurrentUserId from '../../hooks/useCurrentUserId'
import AvailabilityStep from './avaliability_step'
import DescriptionStep from './description_step'
import LocationStep from './location_step'
import PhotoStep from './photo_step'
import { StepName } from './types'
import Container from '../container'
import Typography from '../typography'
import Stepper, { Step, StepContent, StepLabel } from '../stepper'
import Box from '../box'
import Button from '../button'
import Loading from '../loading'
import { CreateStudioInput, IStudioModelWithAvailability } from '../../apilibrary/types/studio'
import { downloadFile } from '../../utils'
import dayjs from 'dayjs'

const steps = [
  {
    label: StepName.Description,
    content: <DescriptionStep />
  },
  {
    label: StepName.Location,
    content: <LocationStep />
  },
  {
    label: 'Availability',
    content: <AvailabilityStep />
  },
  {
    label: 'Photos',
    content: <PhotoStep />
  }
]

export interface StudioListingContentInput extends CreateStudioInput { }

interface StudioListingFormWizardComponentProps {
  formTitle: string
  onClickSave: (params: StudioListingContentInput) => Promise<void>
  saveButtonLabel: string
}

interface StudioListingFormWizardProps extends StudioListingFormWizardComponentProps {
  formData?: IStudioModelWithAvailability
}

const StudioListingFormWizardComponent: React.FC<StudioListingFormWizardComponentProps> = ({
  formTitle,
  onClickSave,
  saveButtonLabel: saveButtonText
}) => {
  const [currentStepIndex, setStepIndex] = useState(0)
  const [disableCreateButton, setDisableCreateButton] = useState(false)
  const [error, setError] = useState<string>()
  const userId = useCurrentUserId() as string
  const {
    data: descriptionStepData,
    validateStepData: validateDescriptionStep
  } = useContext(DescriptionStepContext)
  const {
    data: locationStepData,
    validateStepData: validateLocationStep
  } = useContext(LocationStepContext)
  const {
    data: availabilityStepData,
    validateStepData: validateAvaliabilityStep
  } = useContext(AvailabilityStepContext)
  const {
    data: photoStepData,
    validateStepData: validatePhotoStep
  } = useContext(PhotoStepContext)
  const {
    validateStepData: validatePaymentStep
  } = useContext(PaymentStepContext)

  const validateAllSteps = async (): Promise<void> => {
    validateDescriptionStep()
    validateLocationStep()
    validateAvaliabilityStep()
    validatePhotoStep()
    validatePaymentStep()
  }

  const getFormInputs = (): StudioListingContentInput => {
    const {
      timezone,
      availability,
      exceptions
    } = availabilityStepData
    return {
      ownerUserId: userId,
      availability: {
        standardAvailability: {
          timezone,
          ...availability
        },
        exceptions: exceptions.map(exception => ({
          isAvailable: exception.isAvailable,
          timeRange: {
            startDate: exception.timeRange.startDate.toDate(),
            endDate: exception.timeRange.endDate.toDate()
          }
        }))
      },
      info: {
        title: descriptionStepData.title,
        minSessionMinutes: descriptionStepData.minimumSessionLength,
        studioPictures: photoStepData.photos,
        description: descriptionStepData.description.replaceAll('<p><br></p>', ''),
        rate: descriptionStepData.price,
        maxOccupancy: descriptionStepData.maxOccupancy,
        location: {
          streetAddress: locationStepData.address,
          aptNumber: locationStepData.apt,
          city: locationStepData.city,
          state: locationStepData.state,
          zipCode: locationStepData.zipcode
        }
      }
    }
  }

  const handleClickSave = async (): Promise<void> => {
    try {
      setDisableCreateButton(true)
      await validateAllSteps()
      const input = getFormInputs()
      await onClickSave(input)
    } catch (e) {
      if (e instanceof Error) {
        setError(e.message)
      } else {
        setError('An unknown error occurred')
      }
      setDisableCreateButton(false)
    }
  }

  const currentStepValidator = useMemo(() => {
    if (currentStepIndex >= steps.length) return () => { }
    const currentStepName = steps[currentStepIndex].label
    switch (currentStepName) {
      case StepName.Description:
        return validateDescriptionStep
      case StepName.Location:
        return validateLocationStep
      case StepName.Availability:
        return validateAvaliabilityStep
      case StepName.Photos:
        return validatePhotoStep
      case StepName.Payment:
        return validatePaymentStep
      default:
        return () => { }
    }
  }, [currentStepIndex, validateAvaliabilityStep, validateDescriptionStep, validateLocationStep, validatePaymentStep, validatePhotoStep])

  const handleBack = useCallback((): void => {
    setError(undefined)
    setStepIndex(currentStepIndex - 1);
  }, [currentStepIndex, setStepIndex]);

  const stepNavButtons = useMemo(() => {
    const handleNext = (): void => {
      setError(undefined)
      try {
        currentStepValidator()
        setStepIndex(currentStepIndex + 1);
      } catch (e) {
        if (e instanceof Error) {
          setError(e.message)
        } else {
          setError('An unknown error occurred')
        }
      }
    };

    return (
      <Box mt={2}>
        <Button color='secondary' disabled={currentStepIndex === 0} onClick={handleBack}>
          Back
        </Button>
        <Button color='primary' disabled={currentStepIndex === steps.length} onClick={handleNext}>
          Next
        </Button>
      </Box>
    )
  }, [currentStepIndex, currentStepValidator, handleBack, setStepIndex])

  return (
    <Container maxWidth="md">
      <Typography mb={3} fontSize={32} variant="h2">{formTitle}</Typography>
      <Stepper activeStep={currentStepIndex} orientation='vertical'>
        {steps.map((step) => {
          return (
            <Step key={step.label}>
              <Box>
                <StepLabel>
                  <Typography variant='subtitle2'>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Box mt={2}>
                    {(error !== undefined) && <Typography color='error' gutterBottom>{error}</Typography>}
                    {step.content}
                    {stepNavButtons}
                  </Box>
                </StepContent>
              </Box>
            </Step>
          )
        })}
      </Stepper>
      {currentStepIndex === steps.length && (
        <Box>
          {(error !== undefined) && <Typography color='error' gutterBottom>{error}</Typography>}
          <Button color='secondary' onClick={handleBack} sx={{ marginTop: 3, marginRight: 2 }} variant='contained'>Back</Button>
          <Button sx={{ marginTop: 3 }} disabled={disableCreateButton} onClick={handleClickSave} variant='contained'>{saveButtonText}</Button>
        </Box>
      )}
    </Container>
  )
}

interface InitialFormData {
  description: DescriptionStepFormData
  location: LocationStepFormData
  photos: PhotoStepFormData
  availability: AvailabilityStepFormData
}

const StudioListingFormWizard: React.FC<StudioListingFormWizardProps> = ({
  formData: studio,
  formTitle,
  onClickSave,
  saveButtonLabel
}) => {
  const [initialFormData, setInitialFormData] = useState<InitialFormData | undefined>(undefined)
  const [runningSetup, setRunningSetup] = useState<boolean>(true)

  console.log(studio)

  useEffect(() => {
    if (studio === undefined) {
      setRunningSetup(false)
      return
    }

    const transformStudioToFormData = async (): Promise<void> => {
      const photoFiles = await Promise.all(studio.info.studioPictures.map(async (picture) => {
        const url = await picture.getPublicUrl()
        const file = await downloadFile(url)
        return file
      }))
      const {
        timezone,
        MONDAY = [],
        TUESDAY = [],
        WEDNESDAY = [],
        THURSDAY = [],
        FRIDAY = [],
        SATURDAY = [],
        SUNDAY = []
      } = studio.availability.standardAvailability

      setInitialFormData({
        description: {
          title: studio.info.title,
          description: studio.info.description.replaceAll('<p><br></p>', ''),
          price: studio.info.rate,
          maxOccupancy: studio.info.maxOccupancy,
          minimumSessionLength: studio.info.minSessionMinutes,
          bookingRules: ''
        },
        availability: {
          timezone,
          availability: {
            MONDAY,
            TUESDAY,
            WEDNESDAY,
            THURSDAY,
            FRIDAY,
            SATURDAY,
            SUNDAY
          },
          exceptions: studio.availability.exceptions.map((exception) => ({
            isAvailable: exception.isAvailable,
            timeRange: {
              startDate: dayjs(exception.timeRange.startDate),
              endDate: dayjs(exception.timeRange.endDate)
            }
          }))
        },
        location: {
          address: studio.info.location.streetAddress,
          apt: studio.info.location.aptNumber ?? '',
          city: studio.info.location.city,
          state: studio.info.location.state,
          zipcode: studio.info.location.zipCode
        },
        photos: {
          photos: photoFiles
        }
      })

      setRunningSetup(false)
    }

    void transformStudioToFormData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (runningSetup) {
    return <Loading />
  }

  return (
    <DescriptionStepProvider initialData={initialFormData?.description}>
      <LocationStepProvider initialData={initialFormData?.location}>
        <AvailabilityStepProvider initialData={initialFormData?.availability}>
          <PhotoStepProvider initialData={initialFormData?.photos}>
            <PaymentStepProvider>
              <StudioListingFormWizardComponent
                formTitle={formTitle}
                onClickSave={onClickSave}
                saveButtonLabel={saveButtonLabel}
              />
            </PaymentStepProvider>
          </PhotoStepProvider>
        </AvailabilityStepProvider>
      </LocationStepProvider>
    </DescriptionStepProvider>
  )
}

export default StudioListingFormWizard
